import { InputAdornment, Stack } from '@mui/material';
import { useState } from 'react';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ShowIcon } from '@/shared/assets/icons/show-icon';
import { Alert } from '@/shared/components/ui/alert/alert';
import { IconButton } from '@/shared/components/ui/button/icon-button/icon-button';
import { TFormInputProps } from '@/shared/components/ui/form/components/form-input/form-input.types';
import { TextField } from '@/shared/components/ui/form/components/text-field/text-field';

export const FormInput = <TFieldValues extends FieldValues>({
  name,
  label,
  placeholder,
  type,
  defaultValue,
  hint,
  onChange,
  slotProps,
  ...rest
}: TFormInputProps<TFieldValues>) => {
  const { t } = useTranslation();
  const { control } = useFormContext<TFieldValues>();
  const isPassword = type === 'password';
  const [showPassword, setShowPassword] = useState(!isPassword);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const getInputSlotProps = (passwordField: boolean, value: string) => {
    if (passwordField && value) {
      return {
        ...slotProps,
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onMouseDown={handleTogglePassword}
                onMouseUp={handleTogglePassword}
                edge="end"
              >
                <ShowIcon />
              </IconButton>
            </InputAdornment>
          ),
        },
      };
    }
    return slotProps;
  };

  const _placeholder =
    placeholder ??
    t('ui:default_placeholder', {
      field: label,
    });

  const getFieldType = () => {
    if (type !== 'password') {
      return type;
    }

    if (showPassword) {
      return 'text';
    }

    return 'password';
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({
        field: { onChange: onFieldChange, ...field },
        fieldState,
      }) => {
        const slotProps = getInputSlotProps(isPassword, field.value);

        return (
          <Stack gap="0.75rem" width="100%">
            <TextField
              type={getFieldType()}
              label={label}
              {...rest}
              {...field}
              onChange={(event) => {
                onFieldChange(event);
                onChange?.(event);
              }}
              value={field.value ?? ''}
              error={!!fieldState?.error}
              helperText={fieldState.error?.message}
              fullWidth
              placeholder={_placeholder}
              slotProps={slotProps}
            />
            {hint && <Alert color="blue" text={hint} />}
          </Stack>
        );
      }}
    />
  );
};
